import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useForm, useWatch } from 'react-hook-form';

import { closeModal } from 'reducers/modalReducer';
import { isValidEmail } from 'utils/validators';
import {
	addUserToPortfolio,
	updatePortfolioUser,
	clearPortfolioErrors,
	fetchPortfolioById,
} from 'data/reducers/portfoliosManagementReducer';
import { checkInvitationEligibility } from 'data/repositories/invitationsRepository';
import { showToast } from 'data/reducers/toastReducer';
import StandardModal from 'modals/StandardModal';
import Spinner from 'components/Spinner';
import Alert from 'view/components/Alert';
import { useOrganizationContext } from 'src/contexts/OrganizationContext';
import { FormInput, FormSelect } from '../components/Form';
import './AddPortfolioUserModal.css';

/**
 * Milliseconds of typing quiet before an eligibility check is issued.
 */
const ELIGIBILITY_DEBOUNCE_MS = 400;

/**
 * Outcomes POST /api/portfolio-sets/{id}/users can report.
 */
const ADD_USER_OUTCOME_ATTACHED = 'attached';
const ADD_USER_OUTCOME_INVITED = 'invited';
const ADD_USER_OUTCOME_UNKNOWN = 'unknown';

/**
 * Reports which outcome the add-user endpoint actually performed, read off
 * the body it returned rather than assumed from the copy.
 *
 * The endpoint answers with the loaded PortfolioSet -- carrying a `users`
 * array -- when it attached the account to the set straight away, and with
 * an OrganizationInvitation -- carrying the invitee `email` and no member
 * list -- when it issued an invitation to be accepted later. Both wire
 * shapes are live: the immediate attach is what the endpoint does today,
 * the invitation is what it does once the backend invitation work lands.
 *
 * @param {object|null|undefined} result Parsed response body.
 * @returns {string} One of the ADD_USER_OUTCOME_* constants.
 */
const readAddUserOutcome = (result) => {
	const payload = result?.data ?? result;

	if (!payload || typeof payload !== 'object') {
		return ADD_USER_OUTCOME_UNKNOWN;
	}

	if (Array.isArray(payload.users)) {
		return ADD_USER_OUTCOME_ATTACHED;
	}

	if (typeof payload.email === 'string') {
		return ADD_USER_OUTCOME_INVITED;
	}

	return ADD_USER_OUTCOME_UNKNOWN;
};

/**
 * Builds the one sentence that describes a successful add-user submit. It is
 * the single source for both the toast and the in-modal success alert, so the
 * two surfaces cannot state different outcomes for the same submission.
 *
 * @param {string} outcome One of the ADD_USER_OUTCOME_* constants.
 * @param {string} email The address the submission was made for.
 * @returns {string} Wording that matches what the response reported.
 */
const buildAddUserSuccessMessage = (outcome, email) => {
	if (outcome === ADD_USER_OUTCOME_INVITED) {
		return `An invitation has been sent to "${email}".`;
	}

	if (outcome === ADD_USER_OUTCOME_ATTACHED) {
		return `"${email}" has been added to this portfolio.`;
	}

	// An unrecognized shape means the outcome is genuinely unknown, so claim
	// neither an invitation nor an immediate add.
	return `Your request for "${email}" was submitted successfully.`;
};

const AddPortfolioUserModal = ({ options }) => {
	const dispatch = useDispatch();
	const { portfolioId, existingUser } = options;
	const isEditMode = !!existingUser;

	const { isAddingUser, isUpdatingUser, addUserError, updateUserError } =
		useSelector((state) => ({
			isAddingUser: state.portfoliosManagement.isAddingUser,
			isUpdatingUser: state.portfoliosManagement.isUpdatingUser,
			addUserError: state.portfoliosManagement.addUserError,
			updateUserError: state.portfoliosManagement.updateUserError,
		}));

	const isProcessing = isAddingUser || isUpdatingUser;
	const error = addUserError || updateUserError;

	// React Hook Form setup
	const { control, handleSubmit } = useForm({
		defaultValues: {
			email: existingUser?.email || '',
			userType: existingUser?.type || existingUser?.role || 'viewer',
		},
		mode: 'onChange',
	});

	// The success sentence for the submit that just completed, or null while
	// no submit has succeeded. Held as the message itself rather than a
	// boolean so the alert and the toast render the same derived wording.
	const [successMessage, setSuccessMessage] = useState(null);

	// Invite-time eligibility precheck. This is a courtesy check only: the
	// backend (OrganizationInvitationController::eligibility) 403s unless the
	// caller is an ORGANIZATION admin, while issuing an invitation itself is
	// allowed for a portfolio-set admin/owner who may not be an org admin. So
	// a 403 here -- or any other error -- must never block the button; it
	// just means this precheck could not run, and the backend decides on
	// submit as it always has.
	const [eligibilityReason, setEligibilityReason] = useState(null);

	// True from the moment the address in the field changes until the check for
	// that address reaches a terminal outcome. Between the keystroke and the
	// verdict there are two windows -- the debounce, and the flight of the
	// request -- in which `eligibilityReason` is null although the verdict is
	// simply not known yet, and a submit in either window would skip the
	// precheck altogether. The gate below reads this as well as the reason, so
	// "unknown" holds the button rather than opening it.
	const [isCheckingEligibility, setIsCheckingEligibility] = useState(false);
	const { activeOrganization } = useOrganizationContext();
	const emailValue = useWatch({ control, name: 'email' });
	const eligibilityTimerRef = useRef(null);
	const eligibilityRequestIdRef = useRef(0);

	useEffect(() => {
		// Any change to the email invalidates the previous check's result
		// immediately, per the requirement that the error/disabled state
		// clears as soon as the email changes.
		//
		// The invalidation is bumped here, on every email change, rather than
		// where the request is issued: a change that schedules no request of
		// its own -- the field cleared, or edited into a malformed address --
		// still has to disown a response already in flight for the previous
		// address, or that response lands against the value now in the field.
		eligibilityRequestIdRef.current += 1;
		const requestId = eligibilityRequestIdRef.current;

		setEligibilityReason(null);

		if (eligibilityTimerRef.current) {
			clearTimeout(eligibilityTimerRef.current);
			eligibilityTimerRef.current = null;
		}

		const trimmedEmail = (emailValue || '').trim();

		// Nothing to check yet: edit mode has no email field, the organization
		// hasn't loaded, or the email isn't shaped like an email yet (an empty
		// or malformed email would 400 the endpoint for no benefit). No check
		// is pending in that case, so the gate must not hold the button.
		if (isEditMode || !activeOrganization?.id || !isValidEmail(trimmedEmail)) {
			setIsCheckingEligibility(false);
			return undefined;
		}

		// Closed here, at the keystroke, rather than when the request is
		// finally issued -- the debounce window is part of the unknown.
		setIsCheckingEligibility(true);

		eligibilityTimerRef.current = setTimeout(() => {
			dispatch(checkInvitationEligibility(activeOrganization.id, trimmedEmail))
				.then((result) => {
					// A response for an email the user has since edited away from
					// is stale; ignore it, and leave the gate to whichever check
					// is current.
					if (requestId !== eligibilityRequestIdRef.current) {
						return;
					}
					// An explicit verdict is authoritative in both directions: an
					// invitable address has to lift a block the admin is already
					// under, not merely leave the previous one standing.
					setEligibilityReason(
						result && result.canBeInvited === false
							? result.reason || null
							: null
					);
					setIsCheckingEligibility(false);
				})
				.catch((error) => {
					// Degrade silently to "allow": a 403 (set-admin who isn't an
					// org admin), a network failure, or a 500 all mean this
					// precheck couldn't run, not that the email is ineligible.
					console.error('Invitation eligibility check failed', {
						organizationId: activeOrganization.id,
						error,
					});

					if (requestId !== eligibilityRequestIdRef.current) {
						return;
					}
					// Failing to run is a terminal outcome too, so the gate has
					// to open again -- fail-open is the whole point of the catch.
					setIsCheckingEligibility(false);
				});
		}, ELIGIBILITY_DEBOUNCE_MS);

		return () => {
			if (eligibilityTimerRef.current) {
				clearTimeout(eligibilityTimerRef.current);
				eligibilityTimerRef.current = null;
			}
		};
	}, [emailValue, activeOrganization?.id, isEditMode, dispatch]);

	// User type options
	const userTypeOptions = [
		{ value: 'viewer', label: 'Viewer' },
		{ value: 'admin', label: 'Admin' },
	];

	// Clear errors when modal opens
	useEffect(() => {
		dispatch(clearPortfolioErrors());
	}, [dispatch]);

	// Form submission handler
	const onSubmit = async (data) => {
		try {
			setSuccessMessage(null);

			let result;
			if (isEditMode) {
				// Update existing user
				result = await dispatch(
					updatePortfolioUser(portfolioId, existingUser.id, {
						userType: data.userType,
					})
				);
			} else {
				// Add new user
				result = await dispatch(
					addUserToPortfolio(portfolioId, {
						email: data.email,
						userType: data.userType,
					})
				);
			}

			if (result) {
				// Describe the outcome the response actually reports, and use
				// that one sentence for every surface this submit produces.
				const outcome = isEditMode
					? ADD_USER_OUTCOME_UNKNOWN
					: readAddUserOutcome(result);

				if (!isEditMode && outcome === ADD_USER_OUTCOME_UNKNOWN) {
					console.warn(
						'Unrecognized add-user response shape; reporting no specific outcome',
						{ portfolioId, result }
					);
				}

				const message = isEditMode
					? `User "${existingUser.email}" role has been updated successfully.`
					: buildAddUserSuccessMessage(outcome, data.email);

				dispatch(
					showToast({
						title: 'Success!',
						body: message,
						type: 'success',
					})
				);

				setSuccessMessage(message);

				dispatch(fetchPortfolioById(portfolioId));

				// Close modal after showing success
				setTimeout(() => {
					dispatch(closeModal());
				}, 1500);
			}
		} catch (error) {
			console.error(`Error ${isEditMode ? 'updating' : 'adding'} user:`, error);
			// Error is handled by the reducer and displayed via error state
		}
	};

	const handleCancel = () => {
		dispatch(closeModal());
	};

	// Same derived sentence as the toast: one submit, one statement of what
	// happened, on whichever surface the admin happens to be looking at.
	const successAlert = (
		<Alert color="success">
			<div>{successMessage}</div>
		</Alert>
	);

	const errorAlert = error && (
		<Alert color="danger">
			<div>{error}</div>
		</Alert>
	);

	return (
		<StandardModal
			title={isEditMode ? 'Edit User Role' : 'Add User to Portfolio'}
			className="violerts-v3-root add-portfolio-user-modal"
			size="md"
			onClose={handleCancel}
			closeOnOutsideClick
			noFooter
		>
			<form onSubmit={handleSubmit(onSubmit)}>
				<div className="modal-body">
					{successMessage && successAlert}
					{errorAlert}
					{isProcessing ? (
						<Spinner />
					) : (
						<div className="">
							<p className="text-muted mb-3">
								{isEditMode
									? 'Update the role for this user in the portfolio.'
									: 'Enter the email address and select the role for the user you want to add to this portfolio.'}
							</p>

							{!isEditMode && (
								<FormInput
									name="email"
									control={control}
									type="email"
									label="Email Address"
									placeholder="Enter user's email address"
									required
									validate={{
										isValidEmail: (value) =>
											isValidEmail(value) ||
											'Please enter a valid email address',
									}}
									autoFocus
									disabled={isProcessing}
								/>
							)}

							{!isEditMode && eligibilityReason && (
								<div className="text-danger mb-3" role="alert">
									{eligibilityReason}
								</div>
							)}

							{/* Add User is held until this address's eligibility
								verdict is known; say so, so the held button does not
								read as a dead control. */}
							{!isEditMode && isCheckingEligibility && !eligibilityReason && (
								<div className="form-text mb-3" role="status">
									Checking this email address...
								</div>
							)}

							{isEditMode && (
								<div className="mb-3">
									<label className="form-label">Email Address</label>
									<input
										type="email"
										className="form-control"
										value={existingUser.email}
										disabled
										readOnly
									/>
									<small className="text-muted">Email cannot be changed</small>
								</div>
							)}

							<FormSelect
								name="userType"
								control={control}
								label="User Role"
								options={userTypeOptions}
								placeholder="Select user role"
								required
								helpText="Viewers can access portfolio properties, Admins can manage the portfolio."
								disabled={isProcessing}
							/>
						</div>
					)}
				</div>

				<div className="modal-footer">
					<button
						type="button"
						className="btn btn-secondary"
						onClick={handleCancel}
						disabled={isProcessing}
					>
						Cancel
					</button>
					<button
						type="submit"
						className="btn btn-primary"
						disabled={
							isProcessing || isCheckingEligibility || !!eligibilityReason
						}
					>
						{isProcessing ? (
							<>
								<span
									className="spinner-border spinner-border-sm me-2"
									role="status"
								/>
								{isEditMode ? 'Updating...' : 'Adding...'}
							</>
						) : (
							<>
								<i
									className={`bi ${
										isEditMode ? 'bi-check-lg' : 'bi-plus-lg'
									} me-2`}
								/>
								{isEditMode ? 'Update User' : 'Add User'}
							</>
						)}
					</button>
				</div>
			</form>
		</StandardModal>
	);
};

export default AddPortfolioUserModal;
